import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Fab,
  Badge,
} from '@mui/material';
import {
  Send,
  Close,
  Chat as ChatIcon,
  Minimize,
} from '@mui/icons-material';
import { chatAPI } from '../../services/api';
import { selectUser } from '../../store/authSlice';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Get company_id from Redux store (logged-in user)
  const user = useSelector(selectUser);
  const companyId = user?.company_id || null;

  const [userId] = useState(() => {
    // Generate or get user ID
    let uid = localStorage.getItem('chatbot_user_id');
    if (!uid) {
      uid = 'user_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('chatbot_user_id', uid);
    }
    return uid;
  });
  const [userName] = useState(() => {
    return localStorage.getItem('chatbot_user_name') || '';
  });

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Welcome message
    if (messages.length === 0) {
      setMessages([{
        type: 'bot',
        text: 'Hello! How Can I Help You?',
        timestamp: new Date()
      }]);
    }
  }, [messages.length]);

  const handleSend = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMsg = {
      type: 'user',
      text: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await chatAPI.sendMessage({
        userId: userId,
        userName: userName || 'Customer',
        message: inputMessage,
        companyId: companyId // Send company_id to backend
      });

      const botMsg = {
        type: 'bot',
        text: response.data.data.message,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMsg]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = {
        type: 'bot',
        text: 'माफ करें, कुछ समस्या हुई। कृपया दोबारा प्रयास करें।',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    // Enter key (without Shift) sends message
    // Shift+Enter or Ctrl+Enter adds new line
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }}>
      {!isOpen ? (
        <Badge badgeContent={1} color="error">
          <Fab
            color="primary"
            onClick={() => setIsOpen(true)}
            sx={{ width: 64, height: 64 }}
          >
            <ChatIcon sx={{ fontSize: 32 }} />
          </Fab>
        </Badge>
      ) : (
        <Paper
          elevation={8}
          sx={{
            width: 380,
            height: 550,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: 'primary.dark' }}>🤖</Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Chatbot
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#4ade80',
                    }}
                  />
                  <Typography variant="caption">Online</Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <IconButton size="small" sx={{ color: 'white' }} onClick={() => setIsOpen(false)}>
                <Minimize />
              </IconButton>
              <IconButton size="small" sx={{ color: 'white' }} onClick={() => setIsOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              bgcolor: 'grey.50',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                  gap: 1,
                }}
              >
                {msg.type === 'bot' && (
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>🤖</Avatar>
                )}
                <Box sx={{ maxWidth: '75%' }}>
                  <Paper
                    sx={{
                      p: 1.5,
                      bgcolor: msg.type === 'user' ? 'primary.main' : 'white',
                      color: msg.type === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                      boxShadow: 1,
                    }}
                  >
                    <Typography variant="body2">{msg.text}</Typography>
                  </Paper>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      textAlign: msg.type === 'user' ? 'right' : 'left',
                      color: 'text.secondary',
                    }}
                  >
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>🤖</Avatar>
                <Paper sx={{ p: 1.5, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'grey.400',
                        animation: 'bounce 1.4s infinite ease-in-out',
                        '@keyframes bounce': {
                          '0%, 80%, 100%': { transform: 'scale(0)' },
                          '40%': { transform: 'scale(1)' },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'grey.400',
                        animation: 'bounce 1.4s infinite ease-in-out',
                        animationDelay: '0.2s',
                        '@keyframes bounce': {
                          '0%, 80%, 100%': { transform: 'scale(0)' },
                          '40%': { transform: 'scale(1)' },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'grey.400',
                        animation: 'bounce 1.4s infinite ease-in-out',
                        animationDelay: '0.4s',
                        '@keyframes bounce': {
                          '0%, 80%, 100%': { transform: 'scale(0)' },
                          '40%': { transform: 'scale(1)' },
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Box sx={{ p: 2, bgcolor: 'white', borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                placeholder="Type your message... (Shift+Enter for new line)"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    padding: '8px 12px',
                  },
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={loading || !inputMessage.trim()}
                sx={{ mb: 0.5 }}
              >
                <Send />
              </IconButton>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center', color: 'text.secondary' }}>
              Powered by W.H.D. an IT Solution Provider
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Chatbot;
